import {useCallback, useEffect, useState} from 'react';

import {Artifact} from 'llm/chat-library/ArtifactTypes';
import {useDocumentArtifact} from 'llm/chat-library/useDocumentArtifact';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import useIsPhoneWidth from 'ui-library/hooks/useIsPhoneWidth';

export interface UseArtifactOptions {
  /** The application state from the agent */
  latestApplicationState: object | null;
  /** Whether the sidepanel is expanded (desktop view) */
  isExpanded: boolean;
}

export interface UseArtifactResult {
  /** The currently active artifact, or null if none */
  activeArtifact: Artifact<unknown> | null;
  /** Whether the artifact is open in phone view */
  isPhoneArtifactOpen: boolean;
  /** Open the artifact (handles phone vs desktop) */
  openArtifact: () => void;
  /** Close the artifact in phone view */
  closePhoneArtifact: () => void;
}

/**
 * Hook for managing artifacts in a chat.
 *
 * Currently supports document artifacts from DataCaptureAgentState.
 * In the future, this can be extended to support other artifact types
 * based on different agent states. If / when chats support multiple
 * artifacts, this may need to be refactored to be useArtifacts,
 * which would return an array of artifacts.
 *
 * @returns Artifact management interface
 */
export function useArtifact({latestApplicationState, isExpanded}: UseArtifactOptions): UseArtifactResult {
  const isPhoneWidth = useIsPhoneWidth();
  const [isPhoneArtifactOpen, setIsPhoneArtifactOpen] = useState<boolean>(false);

  // For now, the only artifact type we support is documents from DataCaptureAgentState
  const activeArtifact = useDocumentArtifact({latestApplicationState});

  // Open the artifact when the sidepanel is expanded or phone artifact is opened
  useEffect(() => {
    if (activeArtifact && (isExpanded || isPhoneArtifactOpen)) {
      activeArtifact.open();
    }
  }, [activeArtifact, isExpanded, isPhoneArtifactOpen]);

  // Handle opening the artifact - different behavior for phone vs desktop
  const openArtifact = useCallback(() => {
    if (isPhoneWidth) {
      setIsPhoneArtifactOpen(true);
    } else {
      SidepanelActions.expand();
    }
  }, [isPhoneWidth]);

  const closePhoneArtifact = useCallback(() => {
    setIsPhoneArtifactOpen(false);
  }, []);

  return {
    activeArtifact,
    isPhoneArtifactOpen,
    openArtifact,
    closePhoneArtifact,
  };
}
